/**
 * QZ Tray Credentials Configuration Dialog
 * 
 * Allows users to paste their certificate and private key
 * for silent printing (no Allow/Block popup)
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Link
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { saveQzCredentials, hasQzCredentials, clearQzCredentials } from './qzTrayService';

const steps = ['Generate Certificate', 'Paste Certificate', 'Paste Private Key'];

const QzTrayCredentialsDialog = ({ open, onClose, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [certificate, setCertificate] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [error, setError] = useState(null);
  const [hasExisting, setHasExisting] = useState(false);

  useEffect(() => {
    if (open) {
      setHasExisting(hasQzCredentials());
    }
  }, [open]);

  const handleNext = () => {
    if (activeStep === 0) {
      setActiveStep(1);
    } else if (activeStep === 1) {
      if (!certificate.includes('-----BEGIN CERTIFICATE-----')) {
        setError('Invalid certificate format. Must start with "-----BEGIN CERTIFICATE-----"');
        return;
      }
      setError(null);
      setActiveStep(2);
    } else if (activeStep === 2) {
      if (!privateKey.includes('-----BEGIN') || !privateKey.includes('KEY-----')) {
        setError('Invalid private key format. Must be a PEM formatted key.');
        return;
      }
      setError(null);
      // Save credentials
      const success = saveQzCredentials(certificate.trim(), privateKey.trim());
      if (success) {
        onSuccess?.();
        handleClose();
      } else {
        setError('Failed to save credentials');
      }
    }
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => prev - 1);
  };

  const handleClose = () => {
    setActiveStep(0);
    setCertificate('');
    setPrivateKey('');
    setError(null);
    onClose();
  };

  const handleClearCredentials = () => {
    clearQzCredentials();
    setHasExisting(false);
  };

  const handlePaste = async (setter) => {
    try {
      const text = await navigator.clipboard.readText();
      setter(text);
    } catch (e) {
      setError('Failed to read clipboard. Please paste manually.');
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Generate Your Certificate
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Follow these steps to generate a certificate for silent printing:
            </Typography>
            <ol style={{ paddingLeft: '20px', lineHeight: 2 }}>
              <li>
                <strong>Right-click QZ Tray icon</strong> in system tray (bottom-right of screen)
              </li>
              <li>
                Go to <strong>Advanced → Site Manager</strong>
              </li>
              <li>
                Click the <strong>+ button</strong> → <strong>Create New</strong>
              </li>
              <li>
                Click <strong>Yes</strong> to all prompts:
                <ul style={{ marginTop: '5px' }}>
                  <li>Yes to create keys</li>
                  <li>Yes to automatically install</li>
                  <li>Yes to copy to override.crt</li>
                </ul>
              </li>
              <li>
                A folder <strong>"QZ Tray Demo Cert"</strong> will appear on your Desktop
              </li>
            </ol>
            <Alert severity="info" sx={{ mt: 2 }}>
              The folder contains two files you'll need:
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li><code>digital-certificate.txt</code> - Your certificate</li>
                <li><code>private-key.pem</code> - Your private key</li>
              </ul>
            </Alert>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Paste Your Certificate
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Open <code>digital-certificate.txt</code> from the "QZ Tray Demo Cert" folder
              and paste its entire contents below:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button
                size="small"
                startIcon={<ContentPasteIcon />}
                onClick={() => handlePaste(setCertificate)}
              >
                Paste from Clipboard
              </Button>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={8}
              value={certificate}
              onChange={(e) => setCertificate(e.target.value)}
              placeholder="-----BEGIN CERTIFICATE-----
MIIFAzCCAuugAwIBAgICEAIwDQYJKoZIhvcNAQEFBQAwgZgx...
-----END CERTIFICATE-----"
              sx={{ fontFamily: 'monospace', fontSize: '12px' }}
            />
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Paste Your Private Key
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Open <code>private-key.pem</code> from the "QZ Tray Demo Cert" folder
              and paste its entire contents below:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button
                size="small"
                startIcon={<ContentPasteIcon />}
                onClick={() => handlePaste(setPrivateKey)}
              >
                Paste from Clipboard
              </Button>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={8}
              value={privateKey}
              onChange={(e) => setPrivateKey(e.target.value)}
              placeholder="-----BEGIN PRIVATE KEY-----
MIIEvgIBADANBgkqhkiG9w0BAQEFAASCBKgwggSkAgEAAoIBAQC0...
-----END PRIVATE KEY-----"
              sx={{ fontFamily: 'monospace', fontSize: '12px' }}
            />
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>Security Note:</strong> The private key is stored in your browser's localStorage.
              This is acceptable for POS terminals but not recommended for shared computers.
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SecurityIcon color="primary" />
        Configure Silent Printing
      </DialogTitle>

      <DialogContent dividers>
        {hasExisting && (
          <Alert 
            severity="success" 
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleClearCredentials}>
                Clear
              </Button>
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon fontSize="small" />
              Certificate already configured. Silent printing should work.
            </Box>
          </Alert>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        {activeStep > 0 && (
          <Button onClick={handleBack}>
            Back
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={
            (activeStep === 1 && !certificate) ||
            (activeStep === 2 && !privateKey)
          }
        >
          {activeStep === steps.length - 1 ? 'Save & Finish' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QzTrayCredentialsDialog;
